import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Hidden,
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import { Edit, DeleteOutline, AddBox, Search } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TenantsServerModal from "../../components/T_tenantModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD_TENTANTS") {
    const tenantsServers = action.payload;
    const newTenantsServers = [];

    tenantsServers.forEach((tenantsServer) => {
      const tenantsServerIndex = state.findIndex((q) => q.id === tenantsServer.id);
      if (tenantsServerIndex !== -1) {
        state[tenantsServerIndex] = tenantsServer;
      } else {
        newTenantsServers.push(tenantsServer);
      }
    });

    return [...state, ...newTenantsServers];
  }

  if (action.type === "UPDATE_TENTANTS") {
    const tenantsServer = action.payload;
    const tenantsServerIndex = state.findIndex((q) => q.id === tenantsServer.id);

    if (tenantsServerIndex !== -1) {
      state[tenantsServerIndex] = tenantsServer;
      return [...state];
    } else {
      return [tenantsServer, ...state];
    }
  }

  if (action.type === "DELETE_TENTANTS") {
    const tenantsServerId = action.payload;

    const tenantsServerIndex = state.findIndex((q) => q.id === tenantsServerId);
    if (tenantsServerIndex !== -1) {
      state.splice(tenantsServerIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const TenantsServers = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [tenantsServers, dispatch] = useReducer(reducer, []);
  const [selectedTenantsServers, setSelectedTenantsServers] = useState(null);
  const [tenantsServersModalOpen, setTenantsServerModalOpen] = useState(false);
  const [deletingTenantsServers, setDeletingTenantsServers] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTenantsServers = async () => {
        try {
          const { data } = await api.get("/tenantsServers/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_TENTANTS", payload: data.tenantsServers });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchTenantsServers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("tenantsServer", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TENTANTS", payload: data.tenantsServer });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_TENTANTS",
          payload: +data.tenantsServerId,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenTenantsServerModal = () => {
    setSelectedTenantsServers(null);
    setTenantsServerModalOpen(true);
  };

  const handleCloseTenantsServerModal = () => {
    setSelectedTenantsServers(null);
    setTenantsServerModalOpen(false);
  };

  const handleEditTenantsServers = (tenantsServer) => {
    setSelectedTenantsServers(tenantsServer);
    setTenantsServerModalOpen(true);
  };

  const handleDeleteTenantsServers = async (tenantsServerId) => {
    try {
      await api.delete(`/tenantsServers/${tenantsServerId}`);
      toast.success(i18n.t("global.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTenantsServers(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingTenantsServers &&
          `${i18n.t("global.deleteTitle")} ${deletingTenantsServers.shortcut
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTenantsServers(deletingTenantsServers.id)}
      >
        {i18n.t("global.deleteMessage")}
      </ConfirmationModal>
      <TenantsServerModal
        open={tenantsServersModalOpen}
        onClose={handleCloseTenantsServerModal}
        aria-labelledby="form-dialog-title"
        tenantsServerId={selectedTenantsServers && selectedTenantsServers.id}
      ></TenantsServerModal>
      <MainHeader>
        <Title>{i18n.t("tenantsServer.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Hidden mdDown>
            <TextField
              placeholder={i18n.t("global.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Hidden>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenTenantsServerModal}
          >
            <AddBox /> <Hidden mdDown>{i18n.t("global.buttons.add")}</Hidden>
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Hidden lgUp>
        <TextField
          placeholder={i18n.t("global.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: "gray" }} />
              </InputAdornment>
            ),
          }}
        />
        <br />
      </Hidden>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                #ID
              </TableCell>
              <TableCell align="center">
                {i18n.t("global.name")}
              </TableCell>
              <TableCell align="center">
                Whatsapp
              </TableCell>
              <TableCell align="center">
                E-mail
              </TableCell>
              <TableCell align="center">
                {i18n.t("global.description")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("global.servers")}
              </TableCell>
              <TableCell align="center">
                Status
              </TableCell>
              <TableCell align="center">
                {i18n.t("global.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {tenantsServers.map((tenantsServer) => (
                <TableRow key={tenantsServer.id}>
                  <TableCell align="center">#{tenantsServer.id}</TableCell>
                  <TableCell align="center">{tenantsServer.dbName}<br /> {tenantsServer.dbServer.name}</TableCell>
                  {/*<TableCell align="center">{tenantsServer.whatsapp}</TableCell>
                  <TableCell align="center">{tenantsServer.email}</TableCell>
                  <TableCell align="center">{tenantsServer.description}</TableCell>

                  <TableCell align="center">{tenantsServer.countServer}</TableCell> */}
                  <TableCell align="center">{tenantsServer.status ? "✅" : "❌"}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditTenantsServers(tenantsServer)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setConfirmModalOpen(true);
                        setDeletingTenantsServers(tenantsServer);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default TenantsServers;
